import { useEffect } from 'react';
import { SupplierCostDocumentUploader } from './SupplierCostDocumentUploader';

interface SupplierCostDocumentUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  projectName: string;
  onUploadComplete?: () => void;
}

export function SupplierCostDocumentUploadModal({
  isOpen,
  onClose,
  projectId,
  projectName,
  onUploadComplete,
}: SupplierCostDocumentUploadModalProps) {
  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [isOpen]);

  // Close on escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    if (isOpen) {
      window.addEventListener('keydown', handleEscape);
      return () => window.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleComplete = () => {
    onUploadComplete?.();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Modal Panel */}
      <div className="relative w-full sm:max-w-2xl max-h-[90vh] bg-white rounded-t-2xl sm:rounded-xl shadow-xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-secondary-200">
          <div>
            <h2 className="text-lg font-semibold text-secondary-900">
              Upload Supplier Documents
            </h2>
            <p className="text-sm text-secondary-500 mt-0.5">
              Adding to: {projectName}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-secondary-100 transition-colors"
            aria-label="Close"
          >
            <svg
              className="w-5 h-5 text-secondary-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          <SupplierCostDocumentUploader
            projectId={projectId}
            onComplete={handleComplete}
          />
        </div>
      </div>
    </div>
  );
}
